import {
  toUtcIsoForSingleDay,
  toUtcIsoRangeForDateRange
} from '@/utility/dateFilterHelper';
import {useState, useRef, useCallback, useEffect} from 'react';

// Normalizes incoming search object types to the API-expected types.
// Example: UI may pass type "text" but API expects "string".
const getType = (obj) => {
  const {type, field} = obj || {};
  if (!type) return type;
  if (field === 'rewardId') {
    return 'integer';
  }
  return typeof type === 'string' && type?.toLowerCase() === 'text'
    ? 'string'
    : type;
};

// Generic table state hook:
// - pagination (`pageIndex`)
// - sorting (`sortingObj`)
// - server search filters (`searchArr`) with debounced updates
export const useDataTable = (
  firstPageIndex,
  isNotUtcDate,
  clearSearchArray,
  initialSortConfig = {}
) => {
  const [sortingObj, setSortingObj] = useState(initialSortConfig);
  const [searchArr, setSearchArr] = useState([]);
  const [pageIndex, setPageIndex] = useState(firstPageIndex || 0);

  // When `handleResetAll` runs with `clearSearchArray=true`, we set this flag.
  // The very next debounced filter update (typically from clearing an input) will
  // drop the full array to avoid re-adding a just-cleared filter.
  const clearArrayRef = useRef(false);

  // Single debounce timer for `setSearchArry`.
  // Using a ref keeps the timer stable across renders.
  const debounceTimerRef = useRef(null);

  const isEmptySearchValue = useCallback((value, searchType) => {
    // Treat empty strings and missing values as "remove filter".
    if (!value || value === '') return true;
    // For dateRange we store value as "from,to".
    // If both dates are empty, we treat it as empty and remove the filter.
    if (
      searchType === 'dateRange' &&
      (value === ',' || value === ',,' || value.split(',').every((d) => !d))
    ) {
      return true;
    }
    return false;
  }, []);

  // Applies add/update/remove semantics for a single search filter.
  // This is used inside the debounced update to ensure we always work off the latest state.
  const updateSearchArray = useCallback(
    (prev, nextSearchObj) => {
      if (
        clearArrayRef.current &&
        isEmptySearchValue(nextSearchObj.value, nextSearchObj.type)
      ) {
        clearArrayRef.current = false;
        return [];
      }

      if (isEmptySearchValue(nextSearchObj.value, nextSearchObj.type)) {
        return prev.filter((filter) => filter.field !== nextSearchObj.field);
      }

      const index = prev.findIndex(
        (filter) => filter.field === nextSearchObj.field
      );
      if (index !== -1) {
        const updatedArray = [...prev];
        updatedArray[index] = nextSearchObj;
        return updatedArray;
      }

      return [...prev, nextSearchObj];
    },
    [isEmptySearchValue]
  );

  // Formats a single-date filter.
  // - If `isNotUtcDate` is true, we keep YYYY-MM-DD
  // - Otherwise we convert to ISO with UTC midnight to match server expectations
  const handleDateType = useCallback(
    (searchObj) => {
      if (!searchObj.value) {
        searchObj.value = '';
        return;
      }
      const dateString = searchObj.value;

      // For local-date inputs, keep YYYY-MM-DD for APIs that expect date-only,
      // otherwise convert local start-of-day -> UTC ISO.
      if (isNotUtcDate) {
        // Some APIs expect a date-only string.
        searchObj.value = dateString.split('T')[0];
      } else {
        // Only isStartDate should be true for a single date, isEndDate should be false
        searchObj.value = toUtcIsoForSingleDay(dateString);
      }
    },
    [isNotUtcDate]
  );

  // Normalize a single filter object to the API-expected structure.
  // This keeps behavior consistent between the debounced `setSearchArry` and any
  // programmatic filter application.
  const normalizeSearchObj = useCallback(
    (searchObj) => {
      if (!searchObj) return null;

      const nextSearchObj = {...searchObj};

      // Convert type if it's a text field
      if (nextSearchObj.type) {
        nextSearchObj.type = getType(nextSearchObj);
      }

      if (nextSearchObj.type === 'date') {
        handleDateType(nextSearchObj);
      }

      if (nextSearchObj.type === 'dateRange') {
        nextSearchObj.value = toUtcIsoRangeForDateRange(nextSearchObj.value);
      }

      return nextSearchObj;
    },
    [handleDateType]
  );

  useEffect(() => {
    // Ensure any pending debounce does not fire after unmount.
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Main entrypoint used by table filters.
  // Debounced to avoid firing server requests on every keystroke.
  // Clearing a filter (empty value) applies immediately so that rapid
  // cross-button clicks or a "Reset All" that iterates multiple columns
  // do not lose earlier clears to debounce cancellation.
  const setSearchArry = useCallback(
    (searchObj) => {
      if (!searchObj) return;

      const nextSearchObj = normalizeSearchObj(searchObj);
      if (!nextSearchObj) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Clearing a filter should take effect right away — no need to wait
      // for additional keystrokes. This also avoids the problem where
      // clearing multiple filters in quick succession loses all but the
      // last clear (because a single debounce timer cancels earlier ones).
      if (isEmptySearchValue(nextSearchObj.value, nextSearchObj.type)) {
        setSearchArr((prev) => updateSearchArray(prev, nextSearchObj));
        return;
      }

      // Debounce the state update so consumers can trigger API calls on `searchArr` changes.
      debounceTimerRef.current = setTimeout(() => {
        setSearchArr((prev) => updateSearchArray(prev, nextSearchObj));
      }, 1000);
    },
    [normalizeSearchObj, updateSearchArray, isEmptySearchValue]
  );

  // `setSearchArry` is intentionally debounced (best for typing into filter inputs).
  // `setSearchArrImmediate` is for programmatic filtering where the UI should update
  // instantly (e.g. clicking a banner/notification). It also clears any pending
  // debounce so an older queued update can't overwrite the immediate filter.
  const setSearchArrImmediate = useCallback(
    (searchObj) => {
      if (!searchObj) return;

      const nextSearchObj = normalizeSearchObj(searchObj);
      if (!nextSearchObj) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setSearchArr((prev) => updateSearchArray(prev, nextSearchObj));
    },
    [normalizeSearchObj, updateSearchArray]
  );

  const resetSearchArr = () => {
    setSearchArr([]);
  };

  const handleResetAll = useCallback(() => {
    // Cancel any pending debounced filter update so it cannot re-add a
    // filter after we clear the array.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (clearSearchArray) {
      clearArrayRef.current = true;
    }
    setSearchArr([]);
    setSortingObj(initialSortConfig || {});
    setPageIndex(firstPageIndex || 0);
  }, [
    clearSearchArray,
    setSortingObj,
    setPageIndex,
    firstPageIndex,
    initialSortConfig
  ]);

  return {
    searchArr,
    sortingObj,
    pageIndex,
    setSortingObj,
    setSearchArry,
    setSearchArrImmediate,
    setPageIndex,
    resetSearchArr,
    handleResetAll
  };
};
